"use client";

import {
  Box,
  Button,
  Grid,
  TextField,
  Paper,
  FormControl,
  Select,
  MenuItem,
  FormLabel,
  FormHelperText,
  Typography,
  Autocomplete,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Gender } from "../../../../utils";
import { useFetchData } from "../../../../api/api/FetchData";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import AddBloodTypeDialog from "./AddBloodTypeDialog";
import useAdd from "../../../../api/useAdd";
import useFetchObjects from "../../../../api/useFetchObjects";
import TicketPrint from "../../Ticketing/TicketPrint";
import AddAppointment from "../Appointments2/addAppointment/AddAppointment";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// Button styling
const buttonStyle = {
  textTransform: "capitalize",
  backgroundColor: "#071952",
};

// Helper function to get current local date/time in a "datetime-local" accepted format.
const getCurrentDateTimeLocal = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = ("0" + (now.getMonth() + 1)).slice(-2);
  const day = ("0" + now.getDate()).slice(-2);
  const hours = ("0" + now.getHours()).slice(-2);
  const minutes = ("0" + now.getMinutes()).slice(-2);
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const NewPatient = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const token = user?.token;
  const navigate = useNavigate();
  const { bloodTypeData } = useFetchData();

  const [nextDosiaId, setNextDosiaId] = useState(null);

  // Fetch next sequential Dosia ID (0, 1, 2, ... displayed as 0000000, 0000001, ...).
  const { data: nextDosiaData, refetch: refetchNextDosiaId } = useFetchObjects(
    ["patients-next-dosia-id"],
    "patients/next-dosia-id/",
    token
  );
  useEffect(() => {
    const id = nextDosiaData?.next_dosia_id;
    if (id != null) {
      setNextDosiaId(id);
    }
  }, [nextDosiaData]);

  // Fetch departments for registration (used to set ticket department).
  const { data: departmentsData } = useFetchObjects(
    ["departments"],
    "departments/",
    token
  );
  const departments = departmentsData?.results || departmentsData || [];

  const [openBloodTypeDialog, setOpenBloodTypeDialog] = useState(false);
  const handleOpenBloodTypeDialog = () => setOpenBloodTypeDialog(true);
  const handleCloseBloodTypeDialog = () => setOpenBloodTypeDialog(false);

  // Form data remains independent until a client is manually selected.
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    gender: "",
    age: "",
    blood_type: "",
    phone: "",
    passport: "",
    address: "",
    dosia_file_id: "",
    registration_date: getCurrentDateTimeLocal(),
    department: "",
  });
  const [errors, setErrors] = useState({});
  const registrationDepartmentRef = useRef(null);

  // ---------------------------
  // Phone Number Formatter for both Searching & Submitting
  // ---------------------------
  const formatPhoneForSearch = (phone) => {
    if (phone.startsWith("+")) return phone;
    if (phone.startsWith("0")) return phone.substring(1);
    return phone;
  };

  // ---------------------------
  // Validation Function
  // ---------------------------
  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    if (!formData.firstName) {
      newErrors.firstName = t("reception.firstNameRequired");
      isValid = false;
    }

    // Dosia ID is now auto-assigned (sequential); no manual validation needed.

    setErrors(newErrors);
    return isValid;
  };

  // ---------------------------
  // Client Search Functionality
  // ---------------------------
  const [filterValue, setFilterValue] = useState("");
  const timerRef = useRef(null);

  useEffect(() => {
    // When a phone number is entered, debounce before setting the filter value.
    const phoneTrim = formData.phone.trim();
    // Only trigger if the phone is not empty and has valid numeric format (optionally starting with +)
    const isValidPhone = phoneTrim !== "" && /^[+]?\d+$/.test(phoneTrim);
    if (isValidPhone) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const formattedPhone = formatPhoneForSearch(phoneTrim);
        setFilterValue(formattedPhone);
      }, 300);
    } else {
      // Clear the filter value when phone is invalid or empty.
      setFilterValue("");
    }
  }, [formData.phone]);

  // Fetch matching client(s) using the phone number.
  const {
    data: clientData,
    isLoading: isLoadingClient,
    isError: isErrorClient,
    refetch: refetchClient,
  } = useFetchObjects(
    ["search-client"],
    `search-client/?contact=${filterValue}`,
    token,
    {
      enabled: false,
    }
  );

  useEffect(() => {
    if (filterValue.trim() !== "") {
      refetchClient();
    }
  }, [filterValue, refetchClient]);

  // ---------------------------
  // Update form with selected client info only when the client is clicked.
  // ---------------------------
  const handleClientSelect = (client) => {
    const fullName = client.full_name || "";
    const nameParts = fullName.split(" ");
    const firstNameFromClient = nameParts[0] || "";
    const lastNameFromClient = nameParts.slice(1).join(" ") || "";
    setFormData((prev) => ({
      ...prev,
      firstName: firstNameFromClient,
      lastName: lastNameFromClient,
      phone: client.contact || "",
      address: client.address || "",
      age: client.age || "",
      gender: client.gender || "",
    }));
  };

  // ---------------------------
  // Form Submission & Input Handling
  // ---------------------------
  const handleSubmit = (event) => {
    event.preventDefault();

    if (validateForm()) {
      registrationDepartmentRef.current = formData.department || null;
      const patientData = {
        firstname: formData.firstName,
        lastname: formData.lastName || null,
        age: formData.age ? formData.age : null,
        gender: formData.gender,
        blood_type: formData.blood_type || null,
        // Use the same formatter for submission.
        phone: formatPhoneForSearch(formData.phone.trim()),
        passport: formData.passport,
        address: formData.address || null,
        dosia_file_id: nextDosiaId != null ? nextDosiaId : (formData.dosia_file_id || null),
        registration_date: formData.registration_date,
      };

      handleAdd(patientData);
    }
  };

  // Updated input change handling: when the phone changes we clear auto-populated client info
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === "phone") {
      // Only clear the filter value without clearing manually entered data
      setFormData((prev) => ({
        ...prev,
        phone: value,
      }));
      setFilterValue("");
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const { handleAdd, resetForm, responseData } = useAdd("patients", token);
  const [printTicketOpen, setPrintTicketOpen] = useState(false);
  const [ticketToPrint, setTicketToPrint] = useState(null);
  const [quickActionOpen, setQuickActionOpen] = useState(false);
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);

  // Fetch ticket when patient is created, then PATCH ticket with department if selected
  useEffect(() => {
    const fetchTicket = async () => {
      if (responseData && responseData.id) {
        try {
          // Wait a bit for the signal to create the ticket
          await new Promise(resolve => setTimeout(resolve, 500));

          // Fetch the most recent ticket for this patient
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL || "http://localhost:9000/api"}/tickets/?patient=${responseData.id}&ordering=-created_at`,
            {
              headers: { Authorization: `Token ${token}` },
            }
          );

          const tickets = response.data?.results || response.data || [];
          if (tickets.length > 0) {
            let ticket = tickets[0];
            const departmentId = registrationDepartmentRef.current;
            if (departmentId) {
              try {
                const patchRes = await axios.patch(
                  `${process.env.REACT_APP_API_URL || "http://localhost:9000/api"}/tickets/${ticket.id}/`,
                  { department: departmentId },
                  { headers: { Authorization: `Token ${token}` } }
                );
                ticket = patchRes.data;
              } catch (patchErr) {
                console.error("Error updating ticket department:", patchErr);
              }
              registrationDepartmentRef.current = null;
            }
            setTicketToPrint(ticket);
            setPrintTicketOpen(true);
          }
        } catch (err) {
          console.error("Error fetching ticket:", err);
        }
      }
    };

    fetchTicket();
  }, [responseData, token]);

  // ---------------------------
  // Reset Form and Clear Found Client on Successful Submission
  // ---------------------------
  useEffect(() => {
    if (resetForm) {
      setFormData({
        firstName: "",
        lastName: "",
        gender: "",
        age: "",
        blood_type: "",
        phone: "",
        passport: "",
        address: "",
        dosia_file_id: "",
        registration_date: getCurrentDateTimeLocal(),
        department: "",
      });
      setFilterValue("");
      refetchNextDosiaId(); // So next registration shows the next Dosia ID
    }
  }, [resetForm, refetchNextDosiaId]);

  return (
    <Grid container maxWidth="xl" mt={3} mb={5}>
      <Grid item xs={12} md={10} margin="0px auto">
        <Paper sx={{ boxShadow: "none", width: "100%" }}>
          {/* Display matching client options only as selectable items */}
          {!isLoadingClient &&
            !isErrorClient &&
            clientData &&
            clientData.length > 0 && (
              <Paper
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: "#e0f7fa",
                  borderRadius: "8px",
                }}
                elevation={2}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("call_center.clientView")}
                </Typography>
                {clientData.map((client) => (
                  <Box
                    key={client.id}
                    sx={{
                      p: 1,
                      mb: 1,
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                      cursor: "pointer",
                    }}
                    onClick={() => handleClientSelect(client)}
                  >
                    <Typography>
                      {t("call_center.fullName")}: {client.full_name}
                    </Typography>
                    <Typography>
                      {t("call_center.address")}:{" "}
                      {client.address ||
                        t("call_center.noAddress", "No Address Provided")}
                    </Typography>
                    <Typography>
                      {t("call_center.contact")}: {client.contact}
                    </Typography>
                    <Typography>
                      {t("call_center.additionalNumber")}: {client.contact_2}
                    </Typography>
                    </Box>
                ))}
              </Paper>
            )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            padding="20px"
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={6}>
                <FormLabel>{t("reception.firstName")}</FormLabel>
                <TextField
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <FormLabel>{t("reception.lastName")}</FormLabel>
                <TextField
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <FormLabel>{t("reception.phone")}</FormLabel>
                <TextField
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <FormLabel>{t("reception.passport")}</FormLabel>
                <TextField
                  name="passport"
                  value={formData.passport}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6}>
                <FormLabel>{t("reception.age")}</FormLabel>
                <TextField
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                  error={!!errors.age}
                  helperText={errors.age}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <FormControl
                  size="small"
                  fullWidth
                  error={!!errors.gender}
                  required
                >
                  <FormLabel>{t("reception.gender")}</FormLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                  >
                    {Gender.map((item, index) => (
                      <MenuItem value={item.id} key={index}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.gender && (
                    <FormHelperText>{errors.gender}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <FormControl size="small" fullWidth>
                  <FormLabel>{t("reception.blood_type")}</FormLabel>
                  <Select
                    name="blood_type"
                    value={formData.blood_type}
                    onChange={handleInputChange}
                  >
                    <MenuItem onClick={handleOpenBloodTypeDialog}>
                      {t("reception.addNew")}
                    </MenuItem>
                    {bloodTypeData.map((item, index) => (
                      <MenuItem value={item.id} key={index}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <FormLabel>{t("reception.dosiaFileId")}</FormLabel>
                <TextField
                  name="dosia_file_id"
                  value={nextDosiaId != null ? String(nextDosiaId).padStart(7, "0") : "—"}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                  helperText={t("reception.dosiaSequential", "Sequential ID (auto-assigned)")}
                />
              </Grid>
              <Grid item xs={12}>
                <FormLabel>{t("reception.address")}</FormLabel>
                <TextField
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  placeholder={t("reception.enterAddress")}
                  error={!!errors.address}
                  helperText={errors.address || t("reception.optional")}
                />
              </Grid>
              {/* Registration Date Field */}
              <Grid item xs={12} sm={12} md={6}>
                <FormLabel>{t("reception.registrationDate")}</FormLabel>
                <TextField
                  type="datetime-local"
                  name="registration_date"
                  value={formData.registration_date}
                  onChange={handleInputChange}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              {/* Department selection – sets ticket department after registration */}
              <Grid item xs={12} sm={12} md={6}>
                <FormControl size="small" fullWidth>
                  <FormLabel>{t("reception.department", { defaultValue: "Department" })}</FormLabel>
                  <Select
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>{t("reception.optional", { defaultValue: "Optional" })}</em>
                    </MenuItem>
                    {departments.map((dept) => (
                      <MenuItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{t("reception.departmentHelp", { defaultValue: "Visit department for this ticket" })}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end">
                  <Button type="submit" variant="contained" style={buttonStyle}>
                    {t("reception.submit")}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <AddBloodTypeDialog
            open={openBloodTypeDialog}
            onClose={handleCloseBloodTypeDialog}
            token={token}
          />
        </Paper>
      </Grid>

      {/* Ticket Print Dialog */}
      <Dialog
        open={printTicketOpen}
        onClose={() => {
          setPrintTicketOpen(false);
          // Show quick action dialog after print dialog closes
          if (ticketToPrint) {
            setTimeout(() => setQuickActionOpen(true), 300);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          {ticketToPrint && (
            <TicketPrint
              ticket={ticketToPrint}
              onClose={() => {
                setPrintTicketOpen(false);
                // Show quick action dialog after print dialog closes
                setTimeout(() => setQuickActionOpen(true), 300);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Action Dialog */}
      <Dialog
        open={quickActionOpen}
        onClose={() => setQuickActionOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>
          {t("reception.patientRegistered", { defaultValue: "Patient Registered Successfully!" })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3, textAlign: "center" }}>
            {t("reception.whatWouldYouLikeToDo", { defaultValue: "What would you like to do next?" })}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={() => {
                setQuickActionOpen(false);
                setAppointmentDialogOpen(true);
              }}
              sx={{ py: 1.5 }}
            >
              {t("reception.createOPD", { defaultValue: "Create OPD Appointment" })}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              fullWidth
              onClick={() => {
                setQuickActionOpen(false);
                if (ticketToPrint) {
                  navigate(`/reception/ticketing/tickets/${ticketToPrint.id}`);
                }
              }}
              sx={{ py: 1.5 }}
            >
              {t("reception.viewTicket", { defaultValue: "View Ticket" })}
            </Button>
            <Button
              variant="text"
              color="inherit"
              size="medium"
              fullWidth
              onClick={() => setQuickActionOpen(false)}
              sx={{ py: 1 }}
            >
              {t("reception.close", { defaultValue: "Close" })}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Appointment Dialog */}
      {ticketToPrint && (
        <AddAppointment
          open={appointmentDialogOpen}
          onClose={() => setAppointmentDialogOpen(false)}
          initialTicket={ticketToPrint}
          initialPatient={responseData}
        />
      )}
    </Grid>
  );
};

export default NewPatient;
